/**
 * kisiPanelService.js — geçici kişi paneli (SMS ile gelen bağlantıyla giren işçi/personel) uçları (backlog #646; backend #644 giriş + oturum, #645 yapabildikleri).
 *
 * ⚠ SÖZLEŞME VARSAYIMDIR: #644 / #645 henüz yazılmadı. Yollar ve alanlar bu görevlerin metninden çıkarıldı; backend yazılırken ya buna uyulur ya da bu dosya ve
 * testi birlikte güncellenir. Başlık adı (`X-kisi-oturum`) da varsayımdır (#644: "kendi başlığı"): FDS kullanıcı oturumundan (X-user-id / X-token) AYRI bir yaka;
 * bu sayfa personel oturumunu hiç göndermez, personel uçları da bu başlığı hiç kabul etmez.
 *
 *   /public/kisi-panel (API'nin /api/v1 DIŞINDA, Basic'ten başka kimlik istemez):
 *   POST  /giris            { token, password, id_last4? } → 200 { oturum, idle_timeout_seconds } · 401 AYNI cevap (yanlış bağlantı da yanlış şifre de) · 423/429 kilitli
 *   POST  /cikis            oturumu kapatır
 *   GET   /ben              { person:{ ad, soyad, firma, fuarlar:[{ad}], language, phone, email, editable:['phone','email'] },
 *                             checklist:[{ key, type:'BELGE'|'KVKK'|'MESLEK'|'BILGI', title, status:'MISSING'|'PENDING'|'APPROVED'|'REJECTED', reason? }] }
 *   PUT   /ben/bilgi        { phone?, email? }                       — kimlik / ad / uyruk DEĞİŞTİRİLEMEZ (personele iletilir)
 *   GET   /belgeler         { files:[{ file_id, type_id, type_name, file_name, status, reject_reason, uploaded_at }],
 *                             types:[{ type_id, name, required, max_mb, locked }] }   — locked: onaylı belge, üzerine yazılamaz
 *   POST  /belgeler         multipart { type_id, file } → yüklenen satır (Beklemede)
 *   GET   /kvkk             [{ key, title, body, accepted, accepted_at }]   — iki AYRI onay (#249)
 *   POST  /kvkk/kabul       { key }
 *   GET   /meslekler        { mine:[{ meslek_id, name, status, certificate:{ status, reject_reason, expires_at }|null }], available:[{ meslek_id, name }] }
 *   POST  /meslekler        { meslek_id } · POST /meslekler/{meslekId}/sertifika multipart { file }
 *
 * Oturum YALNIZ bellekte ve sessionStorage'da durur (localStorage'a asla: sekme kapanınca biter, personel oturumuyla karışmaz). Oturumlu bir çağrı 401 alırsa oturum
 * silinir ve hata `oturumBitti` ile fırlar. Hiçbir cevapta bağlantının ya da kişinin var olup olmadığı ayırt edilmez: giriş hatası tek metindir.
 */
import axios from 'axios';
import { JAVA_BACKEND_ORIGIN, getPublicHeader } from '../Components/Contexts/BackendConstants';

const BASE = `${JAVA_BACKEND_ORIGIN}/public/kisi-panel`;
export const OTURUM_BASLIGI = 'X-kisi-oturum';
const ANAHTAR = 'kisiPanelOturum';
/** Backend bir bekleme süresi söylemezse: bu kadar etkinliksiz kalınca oturum kapanır. */
export const VARSAYILAN_BOSTA_SANIYE = 15 * 60;

// ── oturum deposu ──────────────────────────────────────────────────────────
let bellek = null;
export function oturumuOku() {
  if (bellek) return bellek;
  try {
    const ham = window.sessionStorage.getItem(ANAHTAR);
    bellek = ham ? JSON.parse(ham) : null;
  } catch { bellek = null; }
  return bellek;
}
export function oturumuYaz(deger) {
  bellek = deger;
  try { window.sessionStorage.setItem(ANAHTAR, JSON.stringify(deger)); } catch { /* özel pencere: yalnız bellekte kalır */ }
}
export function oturumuSil() {
  bellek = null;
  try { window.sessionStorage.removeItem(ANAHTAR); } catch { /* yok say */ }
}

// ── saf yardımcılar ────────────────────────────────────────────────────────
/**
 * Bağlantıdaki davet anahtarı: yol (`/kisi-panel/<anahtar>`), sorgu (`?t=` / `?token=`) ya da parça (`#t=` / `#token=` — sunucu günlüklerine ve Referer'a girmediği
 * için en iyisi). Bulunamazsa null. Anahtar yalnız harf / rakam / - / _ olabilir.
 */
export function tokenCikar({ pathname = '', search = '', hash = '' } = {}) {
  const gecerli = (s) => (typeof s === 'string' && /^[A-Za-z0-9_-]{8,256}$/.test(s) ? s : null);
  const sorgu = new URLSearchParams(search);
  const parca = new URLSearchParams(String(hash).replace(/^#/, ''));
  const yoldan = /^\/kisi-panel\/([^/?#]+)/i.exec(pathname);
  return gecerli(parca.get('t')) || gecerli(parca.get('token')) || gecerli(sorgu.get('t')) || gecerli(sorgu.get('token')) || gecerli(yoldan && decodeURIComponent(yoldan[1])) || null;
}

const IZINLI_UZANTI = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;
/** Telefondan gelen dosya: resim ya da PDF, boş değil, sınırın altında. Dönüş: null (uygun) | 'tur' | 'bos' | 'boyut'. */
export function dosyaDenetle(dosya, maxMb = 10) {
  if (!dosya) return 'bos';
  if (!dosya.size) return 'bos';
  const tur = String(dosya.type || '').toLowerCase();
  const turTamam = tur.startsWith('image/') || tur === 'application/pdf' || (!tur && IZINLI_UZANTI.test(dosya.name || ''));
  if (!turTamam) return 'tur';
  if (dosya.size > maxMb * 1024 * 1024) return 'boyut';
  return null;
}

/** HTTP hatasını tek biçime indirir. */
function hataAyikla(error) {
  const govde = error?.response?.data && typeof error.response.data === 'object' ? error.response.data : {};
  const e = new Error(govde.error || govde.message || error?.message || 'Unknown error');
  e.status = error?.response?.status ?? null;
  e.errorKey = govde.errorKey || govde.messageKey || null;
  const bekle = Number(error?.response?.headers?.['retry-after']);
  e.retryAfter = Number.isFinite(bekle) && bekle > 0 ? bekle : (Number.isFinite(Number(govde.retry_after_seconds)) ? Number(govde.retry_after_seconds) : null);
  e.ikinciKontrol = govde.second_check_required === true || govde.errorKey === 'kisi_panel.ikinci_kontrol';
  e.network = !error?.response;
  return e;
}

function oturumluBasliklar() {
  const o = oturumuOku();
  return { ...getPublicHeader(), [OTURUM_BASLIGI]: o ? o.oturum : '' };
}

async function oturumlu(istek) {
  try {
    return (await istek(oturumluBasliklar())).data;
  } catch (error) {
    const e = hataAyikla(error);
    if (e.status === 401) { oturumuSil(); e.oturumBitti = true; }
    throw e;
  }
}

// ── çağrılar ───────────────────────────────────────────────────────────────
/** Giriş: bağlantıdaki anahtar + SMS'teki geçici şifre. Başarıda oturum kaydedilir; hata türleri ayırt edilmez (tek metin). */
export async function girisYap({ token, sifre, kimlikSon }) {
  try {
    const body = { token, password: sifre };
    if (kimlikSon && kimlikSon.trim()) body.id_last4 = kimlikSon.trim();
    const response = await axios.post(`${BASE}/giris`, body, { headers: getPublicHeader() });
    const v = response.data || {};
    if (!v.oturum) throw Object.assign(new Error('no session'), { response: { status: 401, data: {} } });
    const kayit = { oturum: v.oturum, bostaSaniye: Number(v.idle_timeout_seconds) > 0 ? Number(v.idle_timeout_seconds) : VARSAYILAN_BOSTA_SANIYE };
    oturumuYaz(kayit);
    return kayit;
  } catch (error) {
    throw hataAyikla(error);
  }
}

export async function cikisYap() {
  const o = oturumuOku();
  oturumuSil();
  if (!o) return;
  try {
    await axios.post(`${BASE}/cikis`, {}, { headers: { ...getPublicHeader(), [OTURUM_BASLIGI]: o.oturum } });
  } catch { /* oturum zaten silindi; sunucu kendi süresiyle de kapatır */ }
}

export const benOku = () => oturumlu((h) => axios.get(`${BASE}/ben`, { headers: h }));
export const bilgiGuncelle = ({ phone, email }) => oturumlu((h) => axios.put(`${BASE}/ben/bilgi`, { phone, email }, { headers: h }));
export const belgeleriOku = () => oturumlu((h) => axios.get(`${BASE}/belgeler`, { headers: h }));
export const kvkkOku = () => oturumlu((h) => axios.get(`${BASE}/kvkk`, { headers: h }));
export const kvkkKabul = (key) => oturumlu((h) => axios.post(`${BASE}/kvkk/kabul`, { key }, { headers: h }));
export const meslekleriOku = () => oturumlu((h) => axios.get(`${BASE}/meslekler`, { headers: h }));
export const meslekEkle = (meslekId) => oturumlu((h) => axios.post(`${BASE}/meslekler`, { meslek_id: meslekId }, { headers: h }));

function yukle(yol, alanlar, dosya, ilerleme) {
  const form = new FormData();
  Object.entries(alanlar).forEach(([k, v]) => form.append(k, String(v)));
  form.append('file', dosya, dosya.name);
  return oturumlu((h) => axios.post(`${BASE}${yol}`, form, {
    headers: h,
    onUploadProgress: (ev) => { if (ilerleme && ev.total) ilerleme(Math.min(100, Math.round((ev.loaded / ev.total) * 100))); },
  }));
}
export const belgeYukle = (typeId, dosya, ilerleme) => yukle('/belgeler', { type_id: typeId }, dosya, ilerleme);
export const sertifikaYukle = (meslekId, dosya, ilerleme) => yukle(`/meslekler/${meslekId}/sertifika`, {}, dosya, ilerleme);
